import React, { useState, useEffect, useCallback } from 'react';
import {
  Upload as UploadIcon,
  X as CloseIcon,
  MapPin as LocationIcon,
  CheckCircle as CheckIcon,
  AlertCircle as ErrorIcon,
  Trash2 as DeleteIcon,
  Loader2,
  Camera,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import InteractiveLocationMap from '../common/InteractiveLocationMap';

interface PhotoUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (files: File[], location: GeolocationCoordinates) => Promise<void>;
  complaintId: string;
  isRTL?: boolean;
}

interface PhotoPreview {
  file: File;
  preview: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
  open,
  onClose,
  onUpload,
  complaintId,
  isRTL = false,
}) => {
  const { t } = useTranslation();

  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [location, setLocation] = useState<{ latitude: number; longitude: number; accuracy: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Supported file types
  const SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/jpg'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const resetState = useCallback(() => {
    setPhotos([]);
    setLocation(null);
    setUploading(false);
    setUploadProgress(0);
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  // Handler for when user selects location on map
  const handleLocationSelect = useCallback((latitude: number, longitude: number) => {
    setLocation({
      latitude,
      longitude,
      accuracy: 0, // Manual selection has perfect accuracy
    });
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    const validPhotos: PhotoPreview[] = [];
    const errors: string[] = [];

    files.forEach((file) => {
      // Check file type
      if (!SUPPORTED_FORMATS.includes(file.type)) {
        errors.push(t('photoUpload.invalidFormat', { name: file.name }));
        return;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(t('photoUpload.fileTooLarge', { name: file.name }));
        return;
      }

      validPhotos.push({
        file,
        preview: URL.createObjectURL(file),
        status: 'pending',
      });
    });

    if (errors.length > 0) {
      // File validation errors
    }

    setPhotos((prev) => [...prev, ...validPhotos]);

    // Reset file input
    event.target.value = '';
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos((prev) => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  };

  const handleUpload = async () => {
    if (!location) {
      alert(t('photoUpload.locationRequired') || (isRTL ? 'يرجى تحديد الموقع أولاً' : 'Please select a location first'));
      return;
    }

    if (photos.length === 0) {
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const files = photos.map((p) => p.file);

    try {
      // Update photos to uploading status
      setPhotos((prev) =>
        prev.map((p) => ({ ...p, status: 'uploading' as const }))
      );

      // Convert location to GeolocationCoordinates format for compatibility
      const geoLocation = {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null,
      } as GeolocationCoordinates;

      // Call the upload handler
      await onUpload(files, geoLocation);

      // Update all to success
      setPhotos((prev) =>
        prev.map((p) => ({ ...p, status: 'success' as const }))
      );

      setUploadProgress(100);

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      // Update all to error
      setPhotos((prev) =>
        prev.map((p) => ({
          ...p,
          status: 'error' as const,
          error: error.message || t('photoUpload.uploadError'),
        }))
      );
    } finally {
      setUploading(false);
    }
  };

  const canUpload = photos.length > 0 && location !== null && !uploading;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
        {/* Dialog Header */}
        <div className="sticky top-0 bg-gradient-to-r from-brand-600 to-brand-700 px-8 py-6 rounded-t-3xl z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">
                {t('photoUpload.title') || (isRTL ? 'رفع الصور' : 'Upload After Cleaning Photos')}
              </h2>
            </div>
            {!uploading && (
              <button
                onClick={onClose}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
              >
                <CloseIcon className="w-6 h-6 text-white" />
              </button>
            )}
          </div>
        </div>

        {/* Dialog Body */}
        <div className="p-8 space-y-6">
          {/* Interactive Location Selection */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-xl ${location ? 'bg-emerald-500' : 'bg-gray-400'}`}>
                <LocationIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                {t('photoUpload.locationInfo') || (isRTL ? 'معلومات الموقع' : 'Location Information')}
              </h3>
            </div>
            <InteractiveLocationMap
              initialLatitude={location?.latitude}
              initialLongitude={location?.longitude}
              onLocationSelect={handleLocationSelect}
              height={400}
              zoom={15}
              isRTL={isRTL}
            />
          </div>

          {/* File Upload Section */}
          <div>
            <input
              accept="image/jpeg,image/png,image/jpg"
              style={{ display: 'none' }}
              id="photo-upload-input"
              multiple
              type="file"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <label htmlFor="photo-upload-input">
              <div className={`w-full px-6 py-4 border-2 border-dashed rounded-2xl transition-all cursor-pointer
                ${uploading ? 'bg-gray-100 border-gray-300 cursor-not-allowed' : 'bg-brand-50 border-brand-300 hover:bg-brand-100 hover:border-brand-400'}`}
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="p-3 bg-brand-600 rounded-xl">
                    <UploadIcon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-center">
                    <p className="text-brand-900 font-bold text-lg">
                      {t('photoUpload.selectPhotos') || (isRTL ? 'اختر الصور' : 'Select Photos')}
                    </p>
                    <p className="text-gray-600 text-sm mt-1">
                      {t('photoUpload.supportedFormats') || 'Supported formats'}: JPEG, PNG • {t('photoUpload.maxSize') || 'Max size'}: 5MB
                    </p>
                  </div>
                </div>
              </div>
            </label>
          </div>

          {/* Photo Previews */}
          {photos.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {t('photoUpload.selectedPhotos') || (isRTL ? 'الصور المحددة' : 'Selected Photos')} ({photos.length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {photos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={photo.preview}
                      alt={`Preview ${index + 1}`}
                      loading="lazy"
                      className={`w-full h-40 object-cover rounded-xl border-2 transition-all
                        ${photo.status === 'uploading' ? 'opacity-60 border-brand-300' : 'border-gray-200'}
                        ${photo.status === 'success' ? 'border-emerald-500' : ''}
                        ${photo.status === 'error' ? 'border-red-500' : ''}`}
                    />

                    {/* Status Overlay */}
                    {photo.status === 'uploading' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-xl">
                        <Loader2 className="w-10 h-10 text-white animate-spin" />
                      </div>
                    )}

                    {photo.status === 'success' && (
                      <div className="absolute top-2 right-2 bg-emerald-500 rounded-full p-1">
                        <CheckIcon className="w-5 h-5 text-white" />
                      </div>
                    )}

                    {photo.status === 'error' && (
                      <div className="absolute top-2 right-2 bg-red-500 rounded-full p-1">
                        <ErrorIcon className="w-5 h-5 text-white" />
                      </div>
                    )}

                    {/* Delete Button */}
                    {!uploading && photo.status === 'pending' && (
                      <button
                        onClick={() => handleRemovePhoto(index)}
                        className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                      >
                        <DeleteIcon className="w-4 h-4 text-white" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="bg-brand-50 border-2 border-brand-200 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <Loader2 className="w-5 h-5 text-brand-600 animate-spin" />
                <p className="text-brand-900 font-bold">
                  {t('photoUpload.uploading') || (isRTL ? 'جاري الرفع...' : 'Uploading...')}
                </p>
              </div>
              <div className="w-full bg-brand-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-brand-600 to-emerald-600 h-full rounded-full transition-all duration-500"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-brand-700 font-medium mt-2 text-right">{uploadProgress}%</p>
            </div>
          )}

          {/* Instructions */}
          {photos.length === 0 && !uploading && (
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <ErrorIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-blue-800 font-medium">
                  {t('photoUpload.instructions') || (isRTL
                    ? 'انقر على "اختر الصور" لتحديد الصور. سيتم التقاط موقع GPS تلقائياً مع كل صورة.'
                    : 'Click "Select Photos" to choose images. GPS location will be automatically captured with each photo.')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Dialog Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-8 py-6 rounded-b-3xl border-t border-gray-200 flex gap-4">
          <button
            onClick={onClose}
            disabled={uploading}
            className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl
                     hover:bg-gray-50 transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.cancel') || (isRTL ? 'إلغاء' : 'Cancel')}
          </button>
          <button
            onClick={handleUpload}
            disabled={!canUpload}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-700 text-white rounded-xl
                     hover:from-brand-700 hover:to-brand-800 transition-all shadow-lg font-bold
                     flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <UploadIcon className="w-5 h-5" />
            )}
            {t('photoUpload.uploadButton') || (isRTL ? 'رفع' : 'Upload & Complete')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadModal;
